// BSGOTECH — shared content model for the platform.
// Bilingual (pt/en); DE/FR fall back to EN via lc().

import { Bot, PackageSearch, Users, Palmtree, Zap, Home, Package, ShoppingBag, GraduationCap, MessageCircle } from "lucide-react";

// Four primary capabilities (NEXIS is NOT a capability).
export const CAPABILITIES = [
  {
    n: "01",
    slug: "hx-ecosystems",
    to: "/solutions",
    icon: Zap,
    color: "#16a34a",
    title: "HX ECOSYSTEMS",
    sub: { pt: "Energia · Água · Modular · E-Mobility", en: "Energy · Water · Modular · E-Mobility" },
    text: { pt: "Soluções integradas para um futuro mais resiliente.", en: "Integrated solutions for a more resilient future." },
    image: "solar_energy",
  },
  {
    n: "02",
    slug: "global-sourcing",
    to: "/sourcing",
    icon: PackageSearch,
    color: "#dc2626",
    title: "GLOBAL SOURCING",
    sub: { pt: "Produtos · Equipamentos · OEM / ODM", en: "Products · Equipment · OEM / ODM" },
    text: { pt: "Da identificação do fornecedor à coordenação da entrega.", en: "From supplier identification to delivery coordination." },
    image: "mercado_modular",
  },
  {
    n: "03",
    slug: "experts",
    to: "/experts",
    icon: Users,
    color: "#7c3aed",
    title: "TECHNICAL EXPERTS & TRAINING",
    sub: { pt: "Engenheiros · Especialistas · Formadores", en: "Engineers · Specialists · Trainers" },
    text: { pt: "Competência especializada para projetos reais.", en: "Specialized expertise for real projects." },
    image: "energy_install",
  },
  {
    n: "04",
    slug: "eco-collection",
    to: "/eco-collection",
    icon: Palmtree,
    color: "#0891b2",
    title: "ECO COLLECTION",
    sub: { pt: "Experiências · Alojamento · Transfers", en: "Experiences · Accommodation · Transfers" },
    text: { pt: "Destinos autênticos. Comunidades mais fortes.", en: "Authentic destinations. Stronger communities." },
    image: "eco_collection",
  },
];

// NEXIS ASSIST intake options -> routing
export const QUIZ_OPTIONS = [
  { key: "energy", icon: Zap, color: "#16a34a", label: { pt: "Energia & Infraestrutura", en: "Energy & Infrastructure" }, desc: { pt: "Solar, armazenamento, água, energia e infraestrutura.", en: "Solar, storage, water, power and infrastructure." }, to: "/solutions", capability: "HX Ecosystems" },
  { key: "modular", icon: Home, color: "#d97706", label: { pt: "Solução Modular", en: "Modular Solution" }, desc: { pt: "Casas, escritórios, instalações e estruturas modulares.", en: "Homes, offices, facilities and modular structures." }, to: "/solutions", capability: "HX Ecosystems" },
  { key: "product", icon: Package, color: "#dc2626", label: { pt: "Produto ou Equipamento", en: "Product or Equipment" }, desc: { pt: "Fabricantes, fornecedores, equipamentos, OEM/ODM e sourcing.", en: "Manufacturers, suppliers, equipment, OEM/ODM and sourcing." }, to: "/sourcing", capability: "Global Sourcing" },
  { key: "trade", icon: ShoppingBag, color: "#b7791f", label: { pt: "Produtos & Comércio", en: "Products & Trade" }, desc: { pt: "Consumo, catálogo, moda e oportunidades para revenda.", en: "Consumer goods, catalog, fashion and resale opportunities." }, to: "/solutions", capability: "BSGOTECH Trade" },
  { key: "expert", icon: Users, color: "#7c3aed", label: { pt: "Especialista ou Formação", en: "Expert or Training" }, desc: { pt: "Engenheiros, técnicos, especialistas e formadores.", en: "Engineers, technicians, specialists and trainers." }, to: "/experts", capability: "Technical Experts & Training" },
  { key: "tourism", icon: Palmtree, color: "#0891b2", label: { pt: "Turismo & Destino", en: "Tourism & Destination" }, desc: { pt: "Experiências, alojamento, transfers e serviços de destino.", en: "Experiences, accommodation, transfers and destination services." }, to: "/eco-collection", capability: "Eco Collection" },
  { key: "digital", icon: Bot, color: "#2563eb", label: { pt: "Digital / AI / Automação", en: "Digital / AI / Automation" }, desc: { pt: "Software, integrações, IA e automação de processos.", en: "Software, integrations, AI and process automation." }, to: "/nexis", capability: "NEXIS" },
  { key: "other", icon: MessageCircle, color: "#64748b", label: { pt: "Outra Necessidade", en: "Other Need" }, desc: { pt: "Descreva o que procura.", en: "Describe what you are looking for." }, to: "/nexis-quiz", capability: "Custom Request" },
];

// How it works — 6 steps
export const HOW_IT_WORKS = [
  { n: 1, title: { pt: "NECESSIDADE", en: "NEED" }, text: { pt: "Diga-nos o que precisa.", en: "Tell us what you need." } },
  { n: 2, title: { pt: "SOLUÇÃO", en: "SOLUTION" }, text: { pt: "Identificamos ou estruturamos a solução.", en: "We identify or structure the solution." } },
  { n: 3, title: { pt: "SOURCING", en: "SOURCING" }, text: { pt: "Selecionamos capacidades e fornecedores.", en: "We select capabilities and suppliers." } },
  { n: 4, title: { pt: "PROPOSTA", en: "PROPOSAL" }, text: { pt: "Apresentamos condições e próximos passos.", en: "We present terms and next steps." } },
  { n: 5, title: { pt: "APROVAÇÃO", en: "APPROVAL" }, text: { pt: "O cliente valida antes da execução.", en: "The client validates before execution." } },
  { n: 6, title: { pt: "EXECUÇÃO", en: "EXECUTION" }, text: { pt: "Coordenamos, entregamos e acompanhamos.", en: "We coordinate, deliver and follow up." } },
];

// Global network regions — presented by network FUNCTION (not owned offices)
export const REGIONS = [
  { key: "eu", flag: "🇪🇺", name: { pt: "Europa", en: "Europe" }, focus: { pt: "Rede de tecnologia & especialistas", en: "Technology & specialist network" }, color: "#1E3A8A", x: "51%", y: "17%" },
  { key: "cn", flag: "🇨🇳", name: { pt: "China", en: "China" }, focus: { pt: "Sourcing & manufacturing network", en: "Sourcing & manufacturing network" }, color: "#dc2626", x: "80%", y: "27%" },
  { key: "in", flag: "🇮🇳", name: { pt: "Índia", en: "India" }, focus: { pt: "Capacidades digitais & técnicas", en: "Digital & technical capabilities" }, color: "#d97706", x: "72%", y: "36%" },
  { key: "stp", flag: "🇸🇹", name: { pt: "São Tomé & Príncipe", en: "São Tomé & Príncipe" }, focus: { pt: "Operação & implementação", en: "Operation & implementation" }, color: "#16a34a", x: "52%", y: "52%" },
  { key: "cv", flag: "🇨🇻", name: { pt: "Cabo Verde", en: "Cabo Verde" }, focus: { pt: "Desenvolvimento de mercado", en: "Market development" }, color: "#0891b2", x: "47%", y: "42%" },
];

// Fallback partner network (used if API has none). Admin-managed via /partners.
export const FALLBACK_PARTNERS = [
  { name: "Wattzing", category: "Energy Storage / EV Charging", status: "Technology Provider" },
  { name: "UNI-HOUSE", category: "Modular Solutions", status: "Selected Supplier" },
  { name: "Bluesun", category: "Solar / Energy", status: "Technology Provider" },
  { name: "Wise Hustlers", category: "Software / AI / Development", status: "Development Network" },
  { name: "Aaban Solutions", category: "IT / Development", status: "Development Network" },
];

export const PROJECT_STAGES = ["Concept", "In Development", "Pilot", "Active", "Completed"];
